/**
 * Google News RSS Feed Provider
 * 
 * Fetches articles from Google News RSS feeds.
 * Free service, no API keys required.
 * Supports location-based, keyword-based, and general news feeds.
 * 
 * DEVELOPMENT MODE: Includes fallback to mock articles when RSS fails
 */
class GoogleNewsFeedProvider {
  constructor() {
    this.name = 'Google News RSS';
    this.baseUrl = 'https://news.google.com/rss';
    this.isEnabled = true;
    this.localProxyAvailable = false; // Will be set after connectivity test
    this.lastProxyTest = 0; // Rate limiting for proxy tests
    
    // RSS proxy endpoint from environment variable with fallback
    const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
    this.localProxy = `${rssProxyUrl}?url=`;
    
    console.log('📡 Google News RSS Provider initialized with RSS Proxy:', rssProxyUrl);
    
    // REMOVED: Don't test proxy connectivity immediately to prevent spam loops
    // this.testLocalProxyConnectivity();
  }

  /**
   * Test local proxy connectivity with rate limiting
   */
  async testLocalProxyConnectivity() {
    // Rate limiting: Only test once per minute
    const now = Date.now();
    if (this.lastProxyTest && (now - this.lastProxyTest) < 60000) {
      console.log('⏱️ Proxy test skipped - rate limited (last test was less than 1 minute ago)');
      return;
    }
    
    this.lastProxyTest = now;
    console.log('🔬 Testing local proxy connectivity...');
    
    try {
      const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
      const response = await fetch(rssProxyUrl, {
        timeout: 5000
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Local proxy is accessible');
        this.localProxyAvailable = true;
      } else {
        console.warn('⚠️ Local proxy health check failed:', response.status);
        this.localProxyAvailable = false;
      }
    } catch (error) {
      console.warn('⚠️ Local proxy connection failed:', error.message);
      this.localProxyAvailable = false;
    }
  }

  /**
   * Fetch articles from Google News RSS with local proxy first
   */
  async fetchArticles(options = {}) {
    // CACHE BUSTING: Add timestamp to prove new code is running
    console.log(`🚨 CACHE BUSTING TEST: ${new Date().toISOString()} - NEW CODE IS RUNNING!`);
    console.log('🔍 Fetching articles with options:', options);
    
    // Try real RSS feeds with local proxy first
    let allArticles = [];
    let searchedSources = []; // Track what sources we actually searched
    
    try {
      const feeds = this.buildFeedUrls(options);
      console.log(`📡 Attempting to fetch from ${feeds.length} feeds using LOCAL PROXY...`);

      for (const feed of feeds) {
        // Track this source as searched
        searchedSources.push({
          name: feed.name,
          type: feed.type,
          status: 'attempted'
        });

        try {
          console.log(`📰 Fetching from: ${feed.name}`);
          const articles = await this.fetchFromFeedWithProxy(feed.url);
          
          if (articles && articles.length > 0) {
            // Add feed metadata to articles
            const enrichedArticles = articles.map(article => ({
              ...article,
              feedSource: feed.name,
              feedType: feed.type,
              feedLocation: feed.location || null
            }));
            
            allArticles.push(...enrichedArticles);
            console.log(`✅ ${feed.name}: ${articles.length} articles`);
            
            // Update source status to success
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'success';
              searchedSources[sourceIndex].articlesFound = articles.length;
            }
          } else {
            console.log(`ℹ️ ${feed.name}: No articles found (this is expected for crisis-specific searches)`);
            
            // Update source status to empty
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'empty';
              searchedSources[sourceIndex].articlesFound = 0;
            }
          }
          
          // Rate limiting - wait between feeds
          await this.delay(500);
          
        } catch (error) {
          console.warn(`⚠️ Feed ${feed.name} failed:`, error.message);
          
          // Update source status to failed
          const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
          if (sourceIndex >= 0) {
            searchedSources[sourceIndex].status = 'failed';
            searchedSources[sourceIndex].error = error.message;
          }
          
          // Continue to next feed - don't let one failure stop the process
        }
      }

    } catch (error) {
      console.warn('⚠️ RSS feed setup failed:', error.message);
    }

    // REAL BEHAVIOR: Return actual results only, plus metadata about sources checked
    const result = {
      articles: allArticles,
      searchedSources: searchedSources,
      totalSourcesChecked: searchedSources.length,
      successfulSources: searchedSources.filter(s => s.status === 'success').length,
      emptySources: searchedSources.filter(s => s.status === 'empty').length,
      failedSources: searchedSources.filter(s => s.status === 'failed').length
    };

    if (allArticles.length > 0) {
      console.log(`✅ Successfully fetched ${allArticles.length} REAL RSS articles from ${result.successfulSources}/${result.totalSourcesChecked} sources`);
      return result.articles; // Keep existing API for now
    } else {
      console.log(`ℹ️ NO ARTICLES FOUND - Crisis-specific searches returned empty results (this is normal and correct)`);
      console.log(`ℹ️ Searched ${result.totalSourcesChecked} sources: ${result.successfulSources} successful, ${result.emptySources} empty, ${result.failedSources} failed`);
      console.log(`ℹ️ This means Google News has no articles matching your specific crisis keywords`);
      console.log(`ℹ️ System is working correctly - returning empty results`);
      
      // Return empty array but store source info for debugging
      this.lastSearchMetadata = result;
      return []; // Return empty array - no fake articles
    }
  }

  /**
   * Get metadata about the last search performed
   */
  getLastSearchMetadata() {
    return this.lastSearchMetadata || {
      searchedSources: [],
      totalSourcesChecked: 0,
      successfulSources: 0,
      emptySources: 0,
      failedSources: 0
    };
  }

  /**
   * Fetch from RSS feed with local proxy ONLY - NO CORS PROXY FALLBACKS
   */
  async fetchFromFeedWithProxy(feedUrl) {
    console.log('🚨🚨🚨 DEBUG: fetchFromFeedWithProxy called with:', feedUrl);
    console.log('🚨🚨🚨 DEBUG: Local proxy URL configured as:', this.localProxy);
    console.log('🚨🚨🚨 DEBUG: Local proxy available:', this.localProxyAvailable);
    
    // RSS Proxy should always be available in production - removing unnecessary availability check
    // if (this.localProxyAvailable === false) {
    //   console.warn('⚠️⚠️⚠️ LOCAL PROXY MARKED AS UNAVAILABLE - RETURNING EMPTY RESULTS (NO CORS FALLBACK)');
    //   return [];
    // }
    
    // Try local proxy ONLY - NO FALLBACKS TO AVOID CORS SPAM
    try {
      console.log(`🏠🏠🏠 ATTEMPTING LOCAL PROXY FOR: ${feedUrl}`);
      const proxyUrl = `${this.localProxy}${encodeURIComponent(feedUrl)}`;
      console.log(`🔗🔗🔗 LOCAL PROXY URL: ${proxyUrl}`);
      
      console.log('🧪🧪🧪 Testing basic localhost connectivity...');
      
      const response = await Promise.race([
        fetch(proxyUrl, {
          headers: {
            'Accept': 'application/xml, text/xml, application/rss+xml'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 10000) // 10 second timeout
        )
      ]);

      console.log(`📡📡📡 LOCAL PROXY RESPONSE STATUS: ${response.status} ${response.statusText}`);
      console.log(`📡📡📡 LOCAL PROXY RESPONSE HEADERS:`, [...response.headers.entries()]);

      if (!response.ok) {
        throw new Error(`Local proxy failed: HTTP ${response.status} ${response.statusText}`);
      }

      const xmlText = await response.text();
      console.log(`📄📄📄 LOCAL PROXY RETURNED ${xmlText.length} BYTES`);
      
      // More thorough validation of RSS content
      if (!xmlText || xmlText.length < 100) {
        throw new Error('Local proxy returned empty or invalid RSS data');
      }
      
      const articles = this.parseRSSFeed(xmlText, feedUrl);
      
      if (articles.length > 0) {
        console.log(`✅✅✅ LOCAL PROXY SUCCEEDED WITH ${articles.length} ARTICLES`);
        return articles;
      } else {
        console.log('ℹ️ℹ️ℹ️ Local proxy returned valid XML but no articles found (normal for crisis-specific searches)');
        return []; // Return empty array - this is normal behavior
      }
      
    } catch (error) {
      console.error(`❌❌❌ LOCAL PROXY FAILED WITH ERROR:`, error);
      console.error(`❌❌❌ Error type:`, error.constructor.name);
      console.error(`❌❌❌ Error message:`, error.message);
      
      // Mark local proxy as unavailable for future requests
      this.localProxyAvailable = false;
      
      // Log more details for debugging
      if (error.message === 'Timeout') {
        console.warn('⏰⏰⏰ LOCAL PROXY TIMED OUT AFTER 10 SECONDS');
      } else if (error.message.includes('fetch')) {
        console.error('🌐🌐🌐 NETWORK ERROR ACCESSING LOCAL PROXY - IS IT RUNNING ON PORT 3001?');
      } else if (error.message.includes('CORS')) {
        console.error('🚫🚫🚫 CORS ERROR - REACT APP CANNOT ACCESS LOCAL PROXY');
      }
      
      // RETURN EMPTY RESULTS - NO CORS PROXY FALLBACKS TO AVOID 403 SPAM
      console.log('🛑🛑🛑 LOCAL PROXY FAILED - RETURNING EMPTY RESULTS (NO CORS FALLBACK TO AVOID 403 ERRORS)');
      return [];
    }
  }

  /**
   * Build feed URLs based on options - ULTRA-SPECIFIC SEARCHES WITH PROPER AND LOGIC
   */
  buildFeedUrls(options = {}) {
    const feeds = [];

    console.log('🔍 Building ULTRA-SPECIFIC feeds with PROPER AND LOGIC:', options);
    
    // DEBUG: Check each condition individually
    console.log('🔍 DEBUG organisations check:', {
      exists: !!options.organisations,
      isArray: Array.isArray(options.organisations),
      length: options.organisations?.length,
      value: options.organisations
    });
    console.log('🔍 DEBUG locations check:', {
      exists: !!options.locations,
      isArray: Array.isArray(options.locations),
      length: options.locations?.length,
      value: options.locations
    });
    console.log('🔍 DEBUG crisisType check:', {
      exists: !!options.crisisType,
      type: typeof options.crisisType,
      value: options.crisisType
    });

    // FLEXIBLE SEARCH STRATEGIES - ORGANIZATION MENTION IS BONUS, NOT REQUIRED!
    const hasOrganisations = options.organisations && options.organisations.length > 0;
    const hasLocations = options.locations && options.locations.length > 0;
    const hasCrisisType = !!options.crisisType && options.crisisType !== 'Other';
    
    console.log('🔍 FLEXIBLE search conditions:', {
      hasOrganisations,
      hasLocations,
      hasCrisisType,
      crisisType: options.crisisType
    });

    // PRIORITY 1: LOCATION + CRISIS (WITHOUT requiring organization - KEY FIX!)
    if (hasLocations && hasCrisisType) {
      for (const location of options.locations) {
        if (location && location.trim().length > 2) {
          // Just location + crisis type - NO ORG REQUIREMENT!
          const locationCrisis = `"${location.trim()}" "${options.crisisType}"`;
          feeds.push({
            name: `🎯 Location + Crisis: "${location}" "${options.crisisType}"`,
            type: 'location_crisis',
            priority: 1,
            location: location,
            crisisType: options.crisisType,
            url: `${this.baseUrl}/search?q=${encodeURIComponent(locationCrisis)}&hl=en-AU&gl=AU&ceid=AU:en&cr=countryAU`
          });
        }
      }
    }

    // PRIORITY 2: LOCATION + EMERGENCY TERMS (broad but relevant)
    if (hasLocations) {
      for (const location of options.locations) {
        if (location && location.trim().length > 2) {
          // Location + emergency/severe/weather terms
          const emergencyTerms = `"${location.trim()}" (emergency OR severe OR weather OR storm OR flood OR missing OR incident)`;
          feeds.push({
            name: `🚨 Emergency Search: "${location}" + emergency keywords`,
            type: 'location_emergency',
            priority: 2,
            location: location,
            url: `${this.baseUrl}/search?q=${encodeURIComponent(emergencyTerms)}&hl=en-AU&gl=AU&ceid=AU:en&cr=countryAU`
          });
        }
      }
    }

    // PRIORITY 3: ORG + LOCATION + CRISIS (bonus when org mentioned) 
    if (hasOrganisations && hasLocations && hasCrisisType) {
      for (const org of options.organisations) {
        for (const location of options.locations) {
          if (org && org.trim().length > 2 && location && location.trim().length > 2) {
            // ORG + LOCATION + CRISIS (high relevance but not required)
            const orgLocationCrisis = `"${org.trim()}" "${location.trim()}" "${options.crisisType}"`;
            feeds.push({
              name: `💎 High Value: "${org}" + "${location}" + "${options.crisisType}"`,
              type: 'org_location_crisis',
              priority: 3,
              organization: org,
              location: location,
              crisisType: options.crisisType,
              url: `${this.baseUrl}/search?q=${encodeURIComponent(orgLocationCrisis)}&hl=en-AU&gl=AU&ceid=AU:en&cr=countryAU`
            });
          }
        }
      }
    }

    // PRIORITY 4: ORG + LOCATION (without strict crisis requirement)
    if (hasOrganisations && hasLocations) {
      for (const org of options.organisations) {
        for (const location of options.locations) {
          if (org && org.trim().length > 2 && location && location.trim().length > 2) {
            const orgLocation = `"${org.trim()}" "${location.trim()}"`;
            feeds.push({
              name: `🏢 Org + Location: "${org}" + "${location}"`,
              type: 'org_location',
              priority: 4,
              organization: org,
              location: location,
              url: `${this.baseUrl}/search?q=${encodeURIComponent(orgLocation)}&hl=en-AU&gl=AU&ceid=AU:en&cr=countryAU`
            });
          }
        }
      }
    }

    console.log(`📡 Built ${feeds.length} FLEXIBLE feed URLs (location-first, org-optional strategy)`);
    return feeds;
  }

  /**
   * Parse RSS XML into article objects
   */
  parseRSSFeed(xmlText, feedUrl) {
    try {
      // Quick validation - check if it looks like XML
      if (!xmlText || !xmlText.trim().startsWith('<')) {
        console.warn('⚠️ Invalid XML response (likely HTML error page)');
        return [];
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.warn('⚠️ XML parsing failed - malformed RSS feed');
        return [];
      }

      const items = xmlDoc.querySelectorAll('item');
      
      if (items.length === 0) {
        console.warn('⚠️ No RSS items found in feed');
        return [];
      }

      const articles = [];

      for (const item of items) {
        try {
          const article = this.parseRSSItem(item);
          if (article) {
            articles.push(article);
          }
        } catch (error) {
          // Don't spam console with individual item errors
          continue;
        }
      }

      return articles;

    } catch (error) {
      console.warn('⚠️ RSS parsing failed:', error.message);
      return [];
    }
  }

  /**
   * Parse individual RSS item
   */
  parseRSSItem(item) {
    try {
      // Extract basic fields
      const title = this.getTextContent(item, 'title');
      const link = this.getTextContent(item, 'link');
      const description = this.getTextContent(item, 'description');
      const pubDate = this.getTextContent(item, 'pubDate');
      const source = this.getTextContent(item, 'source');

      if (!title || !link) {
        return null; // Skip invalid items
      }

      // Parse publication date
      let publishedAt = null;
      if (pubDate) {
        publishedAt = new Date(pubDate);
        if (isNaN(publishedAt.getTime())) {
          publishedAt = new Date(); // Fallback to current time
        }
      } else {
        publishedAt = new Date();
      }

      // Clean up description (remove HTML tags)
      const cleanDescription = this.stripHtml(description);

      // Extract location from content (simple pattern matching)
      const location = this.extractLocation(title, cleanDescription);

      // Determine source name
      let sourceName = 'Google News';
      if (source) {
        sourceName = source;
      } else {
        // Try to extract from URL
        try {
          const url = new URL(link);
          sourceName = url.hostname.replace('www.', '');
        } catch (e) {
          // Keep default
        }
      }

      return {
        title: title.trim(),
        url: link.trim(),
        content: cleanDescription?.substring(0, 500) || '', // Limit content length
        source: sourceName,
        publishedAt: publishedAt,
        location: location,
        language: 'en',
        country: 'AU',
        sentiment: null, // Will be processed later
        relevanceScore: 0, // Will be calculated later
        keywords: [], // Will be extracted later
        category: 'news',
        rawData: {
          originalDescription: description,
          pubDate: pubDate,
          feedUrl: item.baseURI || 'unknown'
        }
      };

    } catch (error) {
      console.error('❌ Error parsing RSS item:', error);
      return null;
    }
  }

  /**
   * Get text content from XML element
   */
  getTextContent(item, tagName) {
    const element = item.querySelector(tagName);
    return element ? element.textContent.trim() : null;
  }

  /**
   * Strip HTML tags from text
   */
  stripHtml(html) {
    if (!html) return '';
    
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  /**
   * Extract location from article text (simple pattern matching)
   */
  extractLocation(title, content) {
    const text = `${title} ${content}`.toLowerCase();
    
    // Australian cities and states
    const locations = [
      'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'darwin', 'canberra',
      'nsw', 'vic', 'qld', 'wa', 'sa', 'nt', 'act', 'tas',
      'new south wales', 'victoria', 'queensland', 'western australia',
      'south australia', 'northern territory', 'australian capital territory', 'tasmania'
    ];

    for (const location of locations) {
      if (text.includes(location)) {
        return location.charAt(0).toUpperCase() + location.slice(1);
      }
    }

    return null;
  }

  /**
   * Add delay for rate limiting
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get provider status
   */
  getStatus() {
    return {
      name: this.name,
      isEnabled: this.isEnabled,
      type: 'rss',
      cost: 'free',
      rateLimit: '1 request/second',
      coverage: 'Global news with Australia focus'
    };
  }

  /**
   * Test connection to Google News
   */
  async testConnection() {
    try {
      const testUrl = `${this.baseUrl}?hl=en-AU&gl=AU&ceid=AU:en`;
      const articles = await this.fetchFromFeed(testUrl);
      
      return {
        success: true,
        articlesFound: articles.length,
        message: `Successfully fetched ${articles.length} articles`
      };

    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to connect to Google News RSS'
      };
    }
  }
}

export default GoogleNewsFeedProvider; 